import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Eye, EyeOff } from 'lucide-react';
import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { axiosInstance } from '../../axios';
import { auth } from '../../config';
import { useHeroToast } from '../../hooks';
import {
  FormContainer,
  InfoText,
  LoadingSpinner,
  LoginBox,
  LoginContent,
  LoginHeader,
  MainContainer,
  WelcomeText,
} from '../SignIn/SignIn.styled';

// Reguły hasła MUSZĄ się zgadzać z panelem hotelarza (`ResetPassword.tsx`
// w panel-app) — to jedno konto Firebase, więc rozjazd oznaczałby hasło
// przyjęte w jednym produkcie i odrzucone w drugim.
const MIN_PASSWORD_LENGTH = 8;

const passwordChecks = (password: string) => ({
  length: password.length >= MIN_PASSWORD_LENGTH,
  upper: /[A-Z]/.test(password),
  lower: /[a-z]/.test(password),
  digit: /\d/.test(password),
});

const PASSWORD_RULES: { key: keyof ReturnType<typeof passwordChecks>; label: string }[] = [
  { key: 'length', label: `At least ${MIN_PASSWORD_LENGTH} characters` },
  { key: 'upper', label: 'One uppercase letter' },
  { key: 'lower', label: 'One lowercase letter' },
  { key: 'digit', label: 'One number' },
];

const inputBase = 'w-full rounded-lg border px-4 py-3 text-sm outline-none transition '
  + 'text-gray-900 font-medium';
const inputNormal = 'border-[#d4c5ab] focus:border-[#1a4d6d] bg-white';
const inputError = 'border-red-400 focus:border-red-500 bg-red-50';
const submitButton = 'mt-1 inline-flex w-full items-center justify-center gap-2 rounded-xl '
  + 'bg-gradient-to-r from-[#1a4d6d] to-[#2c5f7d] px-4 py-3 text-sm font-medium text-white '
  + 'shadow-md transition hover:from-[#1a4d6d] hover:via-[#2c5f7d] hover:to-[#c5a572] '
  + 'hover:shadow-lg disabled:opacity-70';

const errorMessage = (error: unknown): string => {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case 'auth/invalid-action-code':
    case 'auth/expired-action-code':
      return 'This link has expired or was already used. Request a new one below.';
    case 'auth/user-not-found':
      return 'No account found for this e-mail address';
    case 'auth/invalid-email':
      return 'Enter a valid e-mail address';
    case 'auth/weak-password':
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters, with an `
        + 'uppercase letter, a lowercase letter and a number';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again in a moment.';
    default:
      return 'Something went wrong, please try again';
  }
};

/**
 * Ustawienie hasła do ACS — jeden ekran obsługujący oba kroki flow:
 *
 *  1. BEZ `oobCode` (wejście z maila zakładającego konto, `.../set-password`):
 *     pole na e-mail → Firebase wysyła wiadomość z linkiem resetującym.
 *  2. Z `oobCode` (link z tej wiadomości): pole na nowe hasło → zapis i przejście
 *     do logowania.
 *
 * `oobCode` w kroku 2 bierze się z linku, który wysyła NASZ backend
 * (`v3-createUser` / `v3-sendPasswordResetLink` przepisują link z Admin SDK na
 * ten adres — patrz `toAcsPasswordLink`). Globalny „action URL" projektu zostaje
 * ustawiony na panel-app i nie trzeba go ruszać.
 *
 * Krok 1 wysyła maila przez klienta (`sendPasswordResetEmail`), więc tam link
 * prowadzi na adres akcji projektu; `continueUrl` wraca na ten widok.
 */
export const SetPassword: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useHeroToast();

  const oobCode = searchParams.get('oobCode') ?? '';

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(!!oobCode);
  const [codeEmail, setCodeEmail] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Kod sprawdzamy od razu — nie ma sensu pokazywać formularza hasła, jeśli link
  // wygasł. Przy nieudanej weryfikacji spadamy do kroku „podaj e-mail".
  useEffect(() => {
    if (!oobCode) return undefined;

    let active = true;
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        if (!active) return;
        setCodeEmail(verifiedEmail);
        setEmail(verifiedEmail);
      })
      .catch((error) => {
        if (!active) return;
        setCodeEmail(null);
        showError(errorMessage(error), 'Could not open the link');
      })
      .finally(() => {
        if (active) setIsVerifying(false);
      });

    return () => {
      active = false;
    };
  }, [oobCode]);

  const handleSendLink = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setFieldError(null);

    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFieldError('Enter a valid e-mail address');
      return;
    }

    setIsLoading(true);
    try {
      // Maila wysyła NASZ backend (własny szablon, własna poczta) i buduje link
      // prowadzący wprost na ten ekran. Kliencki `sendPasswordResetEmail()`
      // wysyłał wiadomość z szablonu skonfigurowanego w konsoli Firebase — jednego
      // dla całego projektu, kierującego do panelu hotelarza.
      //
      // Endpoint odpowiada tak samo niezależnie od tego, czy konto istnieje i czy
      // ma uprawnienia admina, więc sukces nie znaczy „konto istnieje".
      await axiosInstance.post('/v3-sendAcsPasswordReset', { email: trimmed });
      setEmailSent(true);
    } catch (error) {
      showError(errorMessage(error), 'Could not send the message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setFieldError(null);

    const checks = passwordChecks(password);
    if (!Object.values(checks).every(Boolean)) {
      setFieldError('Password does not meet the requirements listed below');
      return;
    }
    if (password !== passwordConfirm) {
      setFieldError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      showSuccess('Your password has been set. You can sign in now.');
      navigate('/login');
    } catch (error) {
      showError(errorMessage(error), 'Could not set the password');
      setIsLoading(false);
    }
  };

  const renderContent = (): React.ReactNode => {
    if (isVerifying) {
      return <InfoText>Checking the link…</InfoText>;
    }

    // ── Krok 2: link z Firebase jest ważny → ustawiamy hasło ──────────────
    if (oobCode && codeEmail) {
      return (
        <>
          <WelcomeText>
            <h2>Set your password</h2>
            <p>{codeEmail}</p>
          </WelcomeText>

          <form onSubmit={handleSetPassword} noValidate>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="set-password" className="text-sm font-medium text-[#1a4d6d]">
                  <span className="mb-1.5 block">New password</span>
                  <div className="relative">
                    <input
                      id="set-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${inputBase} pr-11 ${fieldError ? inputError : inputNormal}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 hover:text-[#1a4d6d] focus:outline-none transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </label>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="set-password-confirm" className="text-sm font-medium text-[#1a4d6d]">
                  <span className="mb-1.5 block">Repeat password</span>
                  <input
                    id="set-password-confirm"
                    type={showPassword ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className={`${inputBase} ${fieldError ? inputError : inputNormal}`}
                    autoComplete="new-password"
                  />
                </label>
                {fieldError && <span className="text-xs text-red-600">{fieldError}</span>}
              </div>

              <ul className="flex flex-col gap-1 text-xs">
                {PASSWORD_RULES.map((rule) => {
                  const met = passwordChecks(password)[rule.key];
                  return (
                    <li
                      key={rule.key}
                      className={met ? 'text-green-600' : 'text-gray-500'}
                    >
                      {met ? '✓' : '•'}
                      {' '}
                      {rule.label}
                    </li>
                  );
                })}
              </ul>

              <button type="submit" disabled={isLoading} className={submitButton}>
                {isLoading && <LoadingSpinner />}
                <span>{isLoading ? 'Saving...' : 'Set password and go to sign in'}</span>
              </button>
            </div>
          </form>
        </>
      );
    }

    // ── Krok 1: prosimy o e-mail i wysyłamy link resetujący ───────────────
    return (
      <>
        <WelcomeText>
          <h2>Set your password</h2>
          <p>Enter your e-mail address and we will send you a link to set your password.</p>
        </WelcomeText>

        {emailSent ? (
          <InfoText>
            We sent a message to
            {' '}
            <strong>{email.trim()}</strong>
            . Open the link from that e-mail to set your password. If you cannot see it,
            check your SPAM folder.
          </InfoText>
        ) : (
          <form onSubmit={handleSendLink} noValidate>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="set-password-email" className="text-sm font-medium text-[#1a4d6d]">
                  <span className="mb-1.5 block">E-mail</span>
                  <input
                    id="set-password-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`${inputBase} ${fieldError ? inputError : inputNormal}`}
                    autoComplete="email"
                  />
                </label>
                {fieldError && <span className="text-xs text-red-600">{fieldError}</span>}
              </div>

              <button type="submit" disabled={isLoading} className={submitButton}>
                {isLoading && <LoadingSpinner />}
                <span>{isLoading ? 'Sending...' : 'Send link'}</span>
              </button>
            </div>
          </form>
        )}
      </>
    );
  };

  return (
    <MainContainer>
      <FormContainer>
        <LoginBox>
          <LoginHeader>
            <img src="/logo-hotelspot.svg" alt="Logo" />
          </LoginHeader>

          <LoginContent>
            {renderContent()}

            <InfoText>
              <Link to="/login" className="text-[#1a4d6d] underline">
                Back to sign in
              </Link>
            </InfoText>
          </LoginContent>
        </LoginBox>
      </FormContainer>
    </MainContainer>
  );
};
